import React, { useState, useEffect, useMemo } from 'react';
import Plot from "react-plotly.js";
import './TabView.css';

const noteColorDict = {
  // Example: Mapping note.tab to color
  "0": "cyan",
  "1": "cyan",
  "2": "cyan",
  "3": "cyan",
  "4": "cyan",
  "L1": "yellow",
  "L2": "yellow",
  "H3": "red",
  // "0": "#2a9d8f",
  // "1": "#2a9d8f",
  // "2": "#2a9d8f",
  // "3": "#2a9d8f",
  // "4": "#2a9d8f",
  // "L1": "#e9c46a",
  // "L2": "#e9c46a",
  // "H3": "#e76f51",
  // Add other mappings
};

const TabView = ({
  currentTime,
  allNotes,
  measureTimes,
  startDelay,
  bpmScalingFactor
}) => {
  const [violinHeight, setViolinHeight] = useState(650);
  const [violinWidth, setViolinWidth] = useState(400);
  const [futureNotes, setFutureNotes] = useState([]);
  const [futureMeasureTimes, setFutureMeasureTimes] = useState([]);
  const displayTimeWindow = 3;
  
  // Binary search helper to find the first index where condition is true
  const binarySearchFirst = (arr, condition) => {
    let low = 0;
    let high = arr.length;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (condition(arr[mid])) {
        high = mid;
      } else {
        low = mid + 1;
      }
    }
    return low;
  };

  // Binary search helper to find the last index where condition is true
  const binarySearchLast = (arr, condition) => {
    let low = 0;
    let high = arr.length;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (condition(arr[mid])) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return low - 1;
  };
  
  useEffect(() => {
    const updateViolinHeight = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const viewportHeight = window.innerHeight || 800;
      const adjustedHeight = Math.max(520, viewportHeight - 240);
      // Compensate for perspective transform increasing apparent size
      const compensatedHeight = adjustedHeight * 0.9; // Reduce by 10% to keep overall height similar
      setViolinHeight(compensatedHeight);
      const viewportWidth = window.innerWidth || 1600;
      const adjustedWidth = Math.max(400, viewportWidth * 0.2); // e.g., 30% of viewport width, min 300, max 400
      setViolinWidth(adjustedWidth);
    };

    updateViolinHeight();
    window.addEventListener('resize', updateViolinHeight);
    return () => window.removeEventListener('resize', updateViolinHeight);
  }, []);

  // Update the notes to display only the next window
  useEffect(() => {
    if (allNotes.length === 0) {
      setFutureNotes([]);
      return;
    }
    const startTime = currentTime - displayTimeWindow - startDelay;
    const endTime = currentTime + displayTimeWindow - startDelay;
    const startIndex = binarySearchFirst(allNotes, note => note.time * bpmScalingFactor >= startTime);
    const endIndex = binarySearchLast(allNotes, note => note.time * bpmScalingFactor < endTime);
    const filteredNotes = allNotes.slice(startIndex, endIndex + 1).filter(note => note.tab !== '-');
    setFutureNotes(filteredNotes);
  }, [currentTime, allNotes, bpmScalingFactor, displayTimeWindow, startDelay]);
  
  useEffect(() => {
    if (measureTimes.length === 0) {
      setFutureMeasureTimes([]);
      return;
    }
    const startTime = currentTime - startDelay;
    const endTime = currentTime + displayTimeWindow * 2 - startDelay;
    const startIndex = binarySearchFirst(measureTimes, measure => measure.start_time * bpmScalingFactor / 2 >= startTime);
    const endIndex = binarySearchLast(measureTimes, measure => measure.start_time * bpmScalingFactor / 2 < endTime);
    const filteredMeasureTimes = measureTimes.slice(startIndex, endIndex + 1);
    setFutureMeasureTimes(filteredMeasureTimes);
  }, [currentTime, measureTimes, bpmScalingFactor, displayTimeWindow, startDelay]);
  // Create traces for the violin strings and notes
  const violinStrings = [1, 2, 3, 4];
  const stringTraces = useMemo(() => violinStrings.map((string) => ({
    y: [currentTime, currentTime + displayTimeWindow],
    x: [string, string],
    mode: "lines",
    line: { color: "lightgray", width: 1 },
    showlegend: false,
    hoverinfo: 'none', // Disable hover template
  })), [currentTime, displayTimeWindow]);
  
  const stringTraces2 = useMemo(() => violinStrings.map((string) => ({
    y: [currentTime, currentTime + displayTimeWindow],
    x: [string-0.01, string-0.01],
    mode: "lines",
    line: { color: "gray", width: 1 },
    showlegend: false,
    hoverinfo: 'none', // Disable hover template
  })), [currentTime, displayTimeWindow]);
  
  const measureTraces = useMemo(() => futureMeasureTimes.map((measure) => ({
    y: [measure.start_time*bpmScalingFactor/2-0.1+startDelay, measure.start_time*bpmScalingFactor/2-0.1+startDelay],
    x: [1, 4],
    mode: "lines+text",  // Add 'text' to the mode to display text
    line: { color: "lightgray", width: 1 },
    text: ['m'+measure.measure_number],  // Add the measure number as text
    textposition: "bottom center",  // Position text below the line
    textfont: {
      size: 12,  // Adjust font size
      color: "lightgray"  // Adjust text color
    },
    showlegend: false,
    hoverinfo: 'none', // Disable hover template
  })), [futureMeasureTimes, bpmScalingFactor, startDelay]);
  
  // Add notes as text in a single trace
  const noteTraces = useMemo(() => {
    if (futureNotes.length === 0) return [];
    return [{
      x: futureNotes.map(note => note.string),
      y: futureNotes.map(note => note.time * bpmScalingFactor + startDelay),
      text: futureNotes.map(note => note.tab),
      mode: "text",
      textposition: "middle center",
      textfont: { 
        size: 30, 
        family: "Arial Black, sans-serif", 
        color: futureNotes.map(note => noteColorDict[note.tab] || "black") 
      },
      showlegend: false,
      hoverinfo: 'none',
    }];
  }, [futureNotes, bpmScalingFactor, startDelay]);
  
  // Add notes as vertical lines, keeping separate traces for correct coloring
  const noteDurationTraces = useMemo(() => futureNotes.map((note) => ({
    y: [note.time * bpmScalingFactor + 0.12 + startDelay, (note.time + note.duration) * bpmScalingFactor - 0.075 + startDelay],
    x: [note.string, note.string],
    mode: "lines",
    line: { 
      color: noteColorDict[note.tab] || "black",
      width: 3,
    },
    hoverinfo: 'none',
    showlegend: false,
  })), [futureNotes, bpmScalingFactor, startDelay]);

  const plotData = useMemo(() => [...stringTraces, ...stringTraces2, ...measureTraces, ...noteDurationTraces, ...noteTraces], [stringTraces, stringTraces2, measureTraces, noteDurationTraces, noteTraces]);

  return (
    <div className="fixed-plot">
      <div
        className="violin-plot-tilt"
      >
        <Plot
          data={plotData}
          layout={{
            yaxis: { 
              range: [currentTime, currentTime + displayTimeWindow], 
              // title: "Time (s)",
              tickmode: 'linear',  // Ensure regular spacing
              dtick: 1,            // Force ticks to appear every 1 unit
              tickformat: 'd',     // Format ticks as integers
              showgrid: false,  // Remove grid lines on the y-axis
              showticklabels: false,
            },
            xaxis: {
              range: [0.5, 4.5],
              // title: "Strings",
              tickvals: [1, 2, 3, 4],
              ticktext: ["G", "D", "A", "E"], // Custom labels for the violin strings
            },
            dragmode: false,
            height: violinHeight,
            width: violinWidth,
            margin: {
              t: 48,
              b: 36,
              l: 100,
              r: 100,
              pad: 0,
            },
            paper_bgcolor: 'rgba(0,0,0,0)', // Make the plot background transparent
            plot_bgcolor: 'rgba(0,0,0,0)',  // Make the plot area background transparent    
          }}
          config={{displayModeBar: false}}
        />
      </div>
    </div>
  );
};

export default TabView;